/**
 * FAQ Drawer Component Controller
 * Strictly adheres to CRO repository guidelines:
 * - Data attributes exclusively for DOM selection (data-faq-*)
 * - Pure business logic functions separated from DOM
 * - Scoped lifecycle with defensive error guards
 * - Zero global leakage
 */

(function initFaqDrawerModule() {
  'use strict';

  /**
   * Pure Business Logic: Filters FAQ item data based on search text and category.
   * @param {Array<{ id: string, question: string, answer: string, category: string }>} items
   * @param {string} query
   * @param {string} category
   * @returns {Set<string>} Set of matching item IDs
   */
  function filterFaqItems(items, query, category) {
    var normalizedQuery = (query || '').trim().toLowerCase();
    var normalizedCategory = (category || 'all').trim().toLowerCase();
    var matchingIds = new Set();

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var itemCat = (item.category || '').toLowerCase();
      var matchesCategory = normalizedCategory === 'all' ||
        itemCat === normalizedCategory ||
        itemCat.includes(normalizedCategory) ||
        normalizedCategory.includes(itemCat);

      var matchesQuery = !normalizedQuery ||
        item.question.toLowerCase().includes(normalizedQuery) ||
        item.answer.toLowerCase().includes(normalizedQuery);

      if (matchesCategory && matchesQuery) {
        matchingIds.add(item.id);
      }
    }

    return matchingIds;
  }

  /**
   * Controller for the FAQ Drawer instance
   */
  function createFaqDrawerController(rootElement) {
    if (!rootElement) return null;

    // Cache DOM Elements via data attributes
    var launcher = rootElement.querySelector('[data-faq-launcher]');
    var panel = rootElement.querySelector('[data-faq-panel]');
    var backdrop = rootElement.querySelector('[data-faq-backdrop]');
    var closeButtons = rootElement.querySelectorAll('[data-faq-close]');
    var searchInput = rootElement.querySelector('[data-faq-search]');
    var searchClear = rootElement.querySelector('[data-faq-search-clear]');
    var categoryChips = rootElement.querySelectorAll('[data-faq-category]');
    var itemElements = rootElement.querySelectorAll('[data-faq-item]');
    var emptyState = rootElement.querySelector('[data-faq-empty]');

    if (!panel || !launcher) {
      return null;
    }

    // Component State
    var state = {
      isOpen: false,
      selectedCategory: 'all',
      searchQuery: ''
    };

    // Extract raw item data for pure business logic operations
    var itemsData = [];
    itemElements.forEach(function extractItemData(element) {
      var id = element.getAttribute('data-faq-item') || '';
      var questionEl = element.querySelector('[data-faq-question]');
      var answerEl = element.querySelector('[data-faq-answer]');
      var category = element.getAttribute('data-faq-category-name') || '';

      itemsData.push({
        id: id,
        question: questionEl ? questionEl.textContent || '' : '',
        answer: answerEl ? answerEl.textContent || '' : '',
        category: category
      });
    });

    /**
     * Updates visibility of all FAQ accordion items based on active filters
     */
    function updateListVisibility() {
      try {
        var visibleIds = filterFaqItems(itemsData, state.searchQuery, state.selectedCategory);
        var hasVisibleItems = visibleIds.size > 0;

        itemElements.forEach(function toggleItemVisibility(element) {
          var id = element.getAttribute('data-faq-item');
          var isMatch = visibleIds.has(id);
          if (isMatch) {
            element.style.display = '';
          } else {
            element.style.display = 'none';
          }
        });

        if (emptyState) {
          if (hasVisibleItems) {
            emptyState.classList.remove('is-visible');
          } else {
            emptyState.classList.add('is-visible');
          }
        }
      } catch (err) {
        console.warn('[FAQ Hub] Visibility update failed safely', err);
      }
    }

    /**
     * Open Drawer
     */
    function openDrawer() {
      state.isOpen = true;
      rootElement.classList.add('is-active');
      launcher.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    /**
     * Close Drawer
     */
    function closeDrawer() {
      state.isOpen = false;
      rootElement.classList.remove('is-active');
      launcher.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      launcher.focus();
    }

    /**
     * Toggle individual accordion item
     */
    function toggleAccordion(itemElement) {
      var isCurrentlyExpanded = itemElement.classList.contains('is-expanded');
      var trigger = itemElement.querySelector('[data-faq-trigger]');

      // Close other accordions for a clean single-open view
      itemElements.forEach(function collapseOthers(other) {
        if (other !== itemElement && other.classList.contains('is-expanded')) {
          other.classList.remove('is-expanded');
          var otherTrigger = other.querySelector('[data-faq-trigger]');
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        }
      });

      if (isCurrentlyExpanded) {
        itemElement.classList.remove('is-expanded');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      } else {
        itemElement.classList.add('is-expanded');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
    }

    // Event Listeners: Launcher toggle
    launcher.addEventListener('click', function handleLauncherClick() {
      if (state.isOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });

    // Event Listeners: Close buttons & backdrop
    closeButtons.forEach(function bindClose(btn) {
      btn.addEventListener('click', closeDrawer);
    });

    if (backdrop) {
      backdrop.addEventListener('click', closeDrawer);
    }

    // Keyboard navigation (Escape key closes drawer)
    document.addEventListener('keydown', function handleGlobalKeyDown(e) {
      if (e.key === 'Escape' && state.isOpen) {
        closeDrawer();
      }
    });

    // Accordion triggers
    itemElements.forEach(function bindAccordionTrigger(itemEl) {
      var trigger = itemEl.querySelector('[data-faq-trigger]');
      if (trigger) {
        trigger.addEventListener('click', function handleAccordionClick() {
          toggleAccordion(itemEl);
        });
      }
    });

    // Category chip filtering
    categoryChips.forEach(function bindCategoryChip(chip) {
      chip.addEventListener('click', function handleCategoryClick() {
        var category = chip.getAttribute('data-faq-category') || 'all';
        state.selectedCategory = category;

        categoryChips.forEach(function updateChipState(c) {
          var isCurrent = c === chip;
          c.classList.toggle('is-active', isCurrent);
          c.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        });

        updateListVisibility();
      });
    });

    // Search input filtering
    if (searchInput) {
      searchInput.addEventListener('input', function handleSearchInput(e) {
        state.searchQuery = e.target.value;
        if (searchClear) {
          searchClear.classList.toggle('is-visible', state.searchQuery.length > 0);
        }
        updateListVisibility();
      });
    }

    // Search clear button
    if (searchClear && searchInput) {
      searchClear.addEventListener('click', function handleSearchClear() {
        searchInput.value = '';
        state.searchQuery = '';
        searchClear.classList.remove('is-visible');
        searchInput.focus();
        updateListVisibility();
      });
    }

    // Sync launcher visibility with Cart Drawer open state
    try {
      var cartDrawerElement = document.querySelector('#cart-drawer') || document.querySelector('theme-drawer');
      var checkCartOpen = function () {
        var isDrawerOpen = Boolean(
          (cartDrawerElement && (cartDrawerElement.hasAttribute('open') || cartDrawerElement.querySelector('dialog[open]'))) ||
          document.querySelector('theme-drawer[open]') ||
          document.querySelector('dialog.theme-drawer__dialog[open]')
        );
        launcher.classList.toggle('is-cart-open', isDrawerOpen);
      };

      if (cartDrawerElement) {
        var cartObserver = new MutationObserver(checkCartOpen);
        cartObserver.observe(cartDrawerElement, { attributes: true, subtree: true, attributeFilter: ['open'] });
      }

      document.addEventListener('theme-drawer:open', checkCartOpen);
      document.addEventListener('theme-drawer:close', checkCartOpen);
    } catch (cartSyncError) {
      console.warn('[FAQ Hub] Cart drawer sync failed safely', cartSyncError);
    }

    return {
      open: openDrawer,
      close: closeDrawer
    };
  }

  /**
   * DOM Ready Initializer
   */
  function init() {
    try {
      var rootElements = document.querySelectorAll('[data-faq-root]');
      rootElements.forEach(function mountFaqDrawer(root) {
        createFaqDrawerController(root);
      });
    } catch (err) {
      console.warn('[FAQ Hub] Initialization failed safely', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
